import {
  Field,
  ForecastPageLimitsResponse,
  useBandRules,
  useInputFieldDisable,
  useInputFieldLabel,
  useInputFieldRulesTyped,
  usePowerLevelsRules,
} from '@e2m-customer-portal/hooks';
import { convertMeasure, InputFieldType, measureEquals, NullableMeasure } from '@e2m-customer-portal/shared';
import { ReactElement, useCallback } from 'react';
import { Deadline } from '../../utils';
import { NumberInput } from '../number-input/NumberInput';

export interface ProductInputProps {
  fullWidth?: boolean;
  disabled?: boolean;
  type: InputFieldType;
  fields: readonly Field[];
  onChange: (fields: readonly Field[]) => void;
  date: Date | number;
  isControlEnergy: boolean;
  deadlines: readonly Deadline[];
  limits: ForecastPageLimitsResponse;
  lowerLimit: NullableMeasure;
  upperLimit: NullableMeasure;
  powerLevels: readonly NullableMeasure[];
}

const NULL_MEASURE = { value: null, unit: 'W' };

export function ProductInput({
  fullWidth,
  disabled,
  type,
  fields,
  onChange,
  date,
  isControlEnergy,
  deadlines,
  limits,
  lowerLimit,
  upperLimit,
  powerLevels,
}: Readonly<ProductInputProps>): ReactElement<ProductInputProps> {
  const label = useInputFieldLabel(type);
  const isDisabled = useInputFieldDisable(fields, deadlines, limits.installedPower, limits.fields, date);
  const { rules } = useInputFieldRulesTyped(fields, limits, lowerLimit, upperLimit, type);
  const bandRules = useBandRules(type, limits);
  const production =
    type === InputFieldType.FCR
      ? (fields.find((f) => f.type === InputFieldType.PRODUCTION)?.value.value ?? 0)
      : undefined;
  const powerLevelRules = usePowerLevelsRules(powerLevels, 'kW', production);

  const mapField = useCallback(
    (field: Field, value: NullableMeasure) => {
      if (
        type === InputFieldType.PRODUCTION &&
        field.type === InputFieldType.FCR &&
        (value.value === 0 || measureEquals(value, limits.installedPower))
      ) {
        const fcrDisabled = isDisabled(InputFieldType.FCR);

        if (!fcrDisabled) {
          return {
            ...field,
            value: { ...field.value, value: 0 },
          };
        }
      }

      if (field.type !== type) {
        return field;
      }

      return {
        ...field,
        value,
        isComputed: false,
      };
    },
    [type, limits.installedPower, isDisabled],
  );

  const handleChange = useCallback(
    (nextValue: NullableMeasure) => onChange(fields.map((f) => mapField(f, nextValue))),
    [mapField, fields, onChange],
  );

  return (
    <NumberInput
      fullWidth={fullWidth}
      required
      noFractions
      notNegative
      name={type}
      label={label}
      value={convertMeasure(fields.find((f) => f.type === type)?.value ?? NULL_MEASURE, 'kW')}
      onChange={handleChange}
      disabled={disabled || isDisabled(type)}
      rules={isControlEnergy ? bandRules : rules}
      powerLevelRules={isControlEnergy ? undefined : powerLevelRules}
    />
  );
}
